// Entrada a un sitio público: correo -> código de 6 dígitos. La respuesta al
// pedir el código es la misma exista o no el correo (nunca se revela). Con un
// límite alcanzado el servidor dice cuánto esperar y se muestra la cuenta.
// El título y el mensaje del primer paso salen del sitio (`login`), siempre
// como texto plano; sin ellos, los de siempre.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { siteFieldClass } from "./blocks/shared";
import { siteErrorMessage } from "./values";

/** Espera tras enviar un código (el servidor deja pedir otro cada 60 s). */
export const RESEND_COOLDOWN_SEC = 60;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Textos de la entrada cuando el sitio no pone los suyos. */
export const SITE_LOGIN_DEFAULT_TITLE = "Entra con tu correo";
export const SITE_LOGIN_DEFAULT_MESSAGE = "Te enviaremos un código para entrar. No necesitas contraseña.";

export interface SiteLoginProps {
  siteName: string;
  logo?: string;
  /** Título y mensaje del sitio (`login`); vacíos, los de siempre. */
  title?: string;
  message?: string;
  /** Dentro de otra página (vista previa del editor): no ocupa toda la pantalla. */
  embedded?: boolean;
  /** Aviso arriba del formulario (p. ej. la sesión venció). */
  notice?: string;
  onRequestCode: (email: string) => Promise<{ retryAfterSec?: number }>;
  onVerify: (email: string, code: string) => Promise<void>;
}

function useCountdown() {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until <= Date.now()) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [until]);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return {
    left,
    start: (sec: number) => {
      const t = Date.now();
      setNow(t);
      setUntil(t + sec * 1000);
    },
  };
}

const clock = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

export function SiteLogin({ siteName, logo, title, message, embedded = false, notice, onRequestCode, onVerify }: SiteLoginProps) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cooldown = useCountdown();
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  const requestCode = async () => {
    const clean = email.trim();
    if (!EMAIL_RE.test(clean)) {
      setError("Escribe un correo válido.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await onRequestCode(clean);
      if (result.retryAfterSec && result.retryAfterSec > 0) {
        cooldown.start(result.retryAfterSec);
        setInfo("Ya pediste un código hace poco. Usa el último que te llegó o espera para pedir otro.");
      } else {
        cooldown.start(RESEND_COOLDOWN_SEC);
        setInfo(null);
      }
      setCode("");
      setStep("code");
    } catch (err) {
      setError(siteErrorMessage(err, "No pudimos enviar el código. Revisa tu conexión e intenta de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value = code) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 6) {
      setError("Escribe los 6 dígitos del código.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onVerify(email.trim(), digits);
    } catch (err) {
      setError(siteErrorMessage(err, "No pudimos comprobar el código. Intenta de nuevo."));
      setBusy(false);
    }
  };

  const onEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!busy) void requestCode();
  };
  const onCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!busy) void verify();
  };

  return (
    <div
      className={cn(
        "flex items-start justify-center px-4 py-10 @min-[40rem]:items-center",
        // En la vista previa llena la columna del marco.
        embedded ? "flex-1" : "min-h-[100dvh]",
      )}
    >
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          {logo ? (
            <img src={logo} alt="" className="h-12 w-auto max-w-[200px] object-contain" />
          ) : (
            <div className="flex size-12 items-center justify-center rounded-lg bg-primary text-lg font-semibold text-primary-foreground">
              {siteName.trim().charAt(0).toUpperCase() || "S"}
            </div>
          )}
          <p className="text-sm font-medium text-muted-foreground">{siteName}</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm @min-[40rem]:p-6">
          {notice && (
            <p className="mb-4 rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground" role="status">
              {notice}
            </p>
          )}

          {step === "email" ? (
            <form onSubmit={onEmailSubmit} noValidate className="space-y-4" data-testid="site-login-email">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold break-words text-foreground" data-testid="site-login-title">
                  {title?.trim() || SITE_LOGIN_DEFAULT_TITLE}
                </h1>
                <p className="text-sm break-words whitespace-pre-line text-muted-foreground" data-testid="site-login-message">
                  {message?.trim() || SITE_LOGIN_DEFAULT_MESSAGE}
                </p>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="site-login-email" className="block text-sm font-medium text-foreground">
                  Correo electrónico
                </label>
                <input
                  id="site-login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  className={siteFieldClass}
                  value={email}
                  aria-invalid={!!error || undefined}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className="h-11 w-full text-base" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Enviarme un código
              </Button>
            </form>
          ) : (
            <form onSubmit={onCodeSubmit} noValidate className="space-y-4" data-testid="site-login-code">
              <div className="space-y-1">
                <div className="mb-2 flex size-9 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Mail className="size-4" aria-hidden />
                </div>
                <h1 className="text-xl font-semibold text-foreground">Te enviamos un código</h1>
                <p className="text-sm text-muted-foreground">
                  Si <span className="font-medium text-foreground break-all">{email.trim()}</span> tiene acceso, te llegará un
                  código de 6 dígitos. Revisa también la carpeta de spam.
                </p>
              </div>
              {info && (
                <p className="rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground" role="status">
                  {info}
                </p>
              )}
              <div className="space-y-1.5">
                <label htmlFor="site-login-code" className="block text-sm font-medium text-foreground">
                  Código
                </label>
                <input
                  id="site-login-code"
                  ref={codeRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  className={cn(siteFieldClass, "pl-[calc(0.75rem+0.5em)] text-center font-mono text-2xl tracking-[0.5em]")}
                  value={code}
                  aria-invalid={!!error || undefined}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setCode(digits);
                    if (error) setError(null);
                    if (digits.length === 6 && !busy) void verify(digits);
                  }}
                  placeholder="000000"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" className="h-11 w-full text-base" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Entrar
              </Button>
              <div className="flex items-center justify-between gap-2 text-sm">
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                  disabled={busy || cooldown.left > 0}
                  onClick={() => void requestCode()}
                >
                  {cooldown.left > 0 ? `Reenviar en ${clock(cooldown.left)}` : "Reenviar código"}
                </button>
                <button
                  type="button"
                  className="text-muted-foreground underline-offset-4 hover:underline"
                  disabled={busy}
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError(null);
                    setInfo(null);
                  }}
                >
                  Cambiar correo
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
